// The library screen's data. Query keys start with the entity kind ('library'), so a change event of that kind
// (the owner's sync or upload) refetches the list, any open file, the mentions and the search (lib/events.ts). The file is
// fetched through the API only; the search words travel in a POST body, never in a URL.
import { queryOptions } from '@tanstack/react-query'
import type { LibraryItem } from '@nw/shared/api/library'
import { api, apiPath, apiResponse } from '../../lib/api.ts'

/** Files up to this size are shown as text in the app; larger ones are download-only. */
export const VIEW_MAX_BYTES = 2 * 1024 * 1024

const LIBRARY_ID = /^lb_[0-9a-hjkmnp-tv-z]{26}$/
const ORGNR = /^\d{9}$/
const PERSON_ID = /^p_[0-9a-f]{32}$/

/**
 * Router validateSearch: the selected item, and the organisation or person whose mentions are listed (ids only in a URL).
 * The organisation number is kept as a number: the router writes a digits-only string quoted (org=%22…%22) and a number
 * bare, and reads either; an organisation number has no leading zero, so the number is exact.
 */
export function parseSearch(search: Record<string, unknown>): { item?: string; org?: number; person?: string } {
  const out: { item?: string; org?: number; person?: string } = {}
  const org = typeof search.org === 'number' ? String(search.org) : search.org
  if (typeof search.item === 'string' && LIBRARY_ID.test(search.item)) out.item = search.item
  if (typeof org === 'string' && ORGNR.test(org)) out.org = Number(org)
  if (typeof search.person === 'string' && PERSON_ID.test(search.person)) out.person = search.person
  return out
}

export const fileUrl = (id: string): string => apiPath('/api/library/:libraryId/file', { libraryId: id })

/** The API serves every text file as text/plain; HTML and Markdown are text here, a PDF or an image is a download. */
export const isViewable = (item: LibraryItem): boolean => item.mime.startsWith('text/') || item.mime === 'application/json'

export const libraryListQuery = queryOptions({
  queryKey: ['library', 'list'] as const,
  queryFn: ({ signal }) => api.get<{ items: LibraryItem[] }>('/api/library', signal),
})

export const fileTextQuery = (id: string) =>
  queryOptions({
    queryKey: ['library', id, 'file'] as const,
    queryFn: async ({ signal }) => (await apiResponse(fileUrl(id), { signal })).text(),
    staleTime: 5 * 60_000,
  })

export interface SearchHit {
  libraryId: string
  title: string
  snippet: string
}
/** `read` false: the file was never read for mentions (not "none found"); `persons.read` false: not read for persons. */
export interface Mentions {
  read: boolean
  orgs: { orgnr: string; name: string | null; count: number }[]
  persons: { visible: false } | { visible: true; read: boolean; items: { personId: string; initials: string; name: string | null; count: number }[] }
}
/** The owner's read of one file's mentions (library.mentionsRefresh); `persons` null: read without the passkey. */
export interface MentionsRead {
  orgs: number
  persons: number | null
}
export interface Uploaded extends LibraryItem {
  indexed: boolean
  mentions: { orgs: number; persons: number }
}

export const searchQuery = (q: string) =>
  queryOptions({
    queryKey: ['library', 'search', q] as const,
    queryFn: ({ signal }) => api.post<{ items: SearchHit[] }>('/api/library/search', { q }, signal),
    staleTime: 30_000,
  })

/** `stepUpUntil` is in the key so the names come again, unmasked, once the owner confirms the passkey. */
export const mentionsQuery = (id: string, stepUpUntil: string | null) =>
  queryOptions({
    queryKey: ['library', id, 'mentions', stepUpUntil] as const,
    queryFn: ({ signal }) => api.get<Mentions>(apiPath('/api/library/:libraryId/mentions', { libraryId: id }), signal),
  })

/** The files that mention an organisation / a register person (the person's list is the owner's). */
export interface MentionedIn {
  items: { libraryId: string; title: string; count: number }[]
}
export const forOrgQuery = (orgnr: string) =>
  queryOptions({
    queryKey: ['library', 'forOrg', orgnr] as const,
    queryFn: ({ signal }) => api.get<MentionedIn>(apiPath('/api/orgs/:orgnr/library', { orgnr }), signal),
  })
export const forPersonQuery = (personId: string) =>
  queryOptions({
    queryKey: ['library', 'forPerson', personId] as const,
    queryFn: ({ signal }) => api.get<MentionedIn>(apiPath('/api/people/:personId/library', { personId }), signal),
  })

/** What the upload takes (CONTRACTS 20.7): the type is told by the extension; the server checks it against the bytes. */
const UPLOAD_TYPES = {
  md: 'text/markdown',
  markdown: 'text/markdown',
  txt: 'text/plain',
  html: 'text/html',
  htm: 'text/html',
  csv: 'text/csv',
  pdf: 'application/pdf',
} as const
export const UPLOAD_ACCEPT = Object.keys(UPLOAD_TYPES).map((e) => `.${e}`).join(',')
export const uploadMime = (fileName: string): (typeof UPLOAD_TYPES)[keyof typeof UPLOAD_TYPES] | null =>
  Object.hasOwn(UPLOAD_TYPES, fileName.split('.').pop()?.toLowerCase() ?? '') ? UPLOAD_TYPES[fileName.split('.').pop()?.toLowerCase() as keyof typeof UPLOAD_TYPES] : null

/** The file's bytes as base64, for the JSON body. */
export function readBase64(file: File): Promise<string> {
  return new Promise((ok, fail) => {
    const reader = new FileReader()
    reader.onload = () => ok(String(reader.result).slice(String(reader.result).indexOf(',') + 1))
    reader.onerror = () => fail(reader.error)
    reader.readAsDataURL(file)
  })
}
